import type { MultiKey } from '../core/MultiKey';
import type { ScenarioLocation } from '../../lib/planningData';
import type {
  Distances,
  OverrideParams,
  RouteData,
  RouteParams,
} from './context';

import type { JSONSpecialObject } from '../core/ExtendedJSON';
import { zeroDistance } from './constant';
import { resolveEffectiveLocation } from './distance-utils';
import type { ClusterId, LocationId } from '@seaber/common/core/entries';

export class DistanceMap implements Distances {
  map: MultiKey<RouteData, RouteParams>;
  locationsById: Map<LocationId, ScenarioLocation>;
  draft: number | null;
  noGoZones: string;
  overrides: MultiKey<RouteData, OverrideParams>;

  constructor(
    map: MultiKey<RouteData, RouteParams>,
    locationsById: Map<LocationId, ScenarioLocation>,
    draft: number | null | undefined,
    noGoZones: string,
    overrides: MultiKey<RouteData, OverrideParams>,
  ) {
    this.map = map;
    this.locationsById = locationsById;
    this.draft = draft ?? null;
    this.noGoZones = noGoZones ?? null;
    this.overrides = overrides ?? null;
  }

  toJS(): JSONSpecialObject {
    return {
      __type: 'DistanceMap',
      value: {
        map: this.map,
        locationsById: this.locationsById,
        draft: this.draft,
        noGoZones: this.noGoZones,
        overrides: this.overrides,
      },
    };
  }

  static fromJS(
    value: (JSONSpecialObject & { __type: 'DistanceMap' })['value'],
  ): DistanceMap {
    const locationsById: Map<LocationId, ScenarioLocation> = new Map(
      Array.from(value.locationsById.entries()).map(([key, val]) => [key, val]),
    );
    return new DistanceMap(
      value.map,
      locationsById,
      value.draft,
      value.noGoZones,
      value.overrides,
    );
  }

  /**
   * Finds route data between two locations, checking for overrides before falling back to cluster-based distance.
   *
   * Priority order:
   * 1. Override from fromLocation to toLocation
   * 2. Override from toLocation to fromLocation
   * 3. Cluster-based distance from the map
   */
  private findRouteData(
    fromLocation: ScenarioLocation,
    toLocation: ScenarioLocation,
    avoidsChannels: boolean,
    draft?: number | null,
    noGoZones?: string,
  ): RouteData | undefined {
    if (!fromLocation.clusterId || !toLocation.clusterId) {
      return zeroDistance;
    }

    const normalOverride = this.overrides.get(
      fromLocation.id,
      toLocation.id,
      avoidsChannels,
    );

    if (normalOverride?.is_override) {
      return normalOverride;
    }

    const reverseOverride = this.overrides.get(
      toLocation.id,
      fromLocation.id,
      avoidsChannels,
    );

    if (reverseOverride?.is_override) {
      return reverseOverride;
    }

    return this.map.get(
      fromLocation.clusterId,
      toLocation.clusterId,
      avoidsChannels,
      draft ?? this.draft,
      noGoZones ?? this.noGoZones,
    );
  }

  get(
    fromLocationId: LocationId,
    toLocationId: LocationId,
    avoidsChannels: boolean,
    draft?: number | null,
    noGoZones?: string,
  ): RouteData | undefined {
    const fromLocation = resolveEffectiveLocation(
      this.locationsById,
      fromLocationId,
    );
    const toLocation = resolveEffectiveLocation(
      this.locationsById,
      toLocationId,
    );

    if (!fromLocation || !toLocation) {
      return undefined;
    }

    if (fromLocation === toLocation) {
      return zeroDistance;
    }

    return this.findRouteData(
      fromLocation,
      toLocation,
      avoidsChannels,
      draft,
      noGoZones,
    );
  }

  set(
    data: RouteData,
    fromClusterId: ClusterId,
    toClusterId: ClusterId,
    avoidsChannels: boolean,
    draft?: number | null,
    noGoZones?: string,
  ): void {
    this.map.set(
      data,
      fromClusterId,
      toClusterId,
      avoidsChannels,
      draft ?? this.draft,
      noGoZones ?? this.noGoZones,
    );
  }
}
